import { csvMoney, csvRate } from '../csvValues';

import type { MetricKey } from './model';

export type MetricColumn = { key: MetricKey; label: string; format?: 'percent' | 'decimal' };

/** AUD measures, exported to the cent. */
export const MONEY_METRICS: ReadonlySet<MetricKey> = new Set<MetricKey>(['sales', 'margin', 'totalMargin', 'adr']);

/** One CSV value: money to the cent, rates and averages to six places, counts as they are. */
export function metricCsvValue(value: number | null | undefined, column: MetricColumn): unknown {
  if (MONEY_METRICS.has(column.key)) return csvMoney(value);
  if (column.format || column.key === 'leadTime') return csvRate(value);
  return value;
}

export const metricColumns: Array<MetricColumn> = [
  { key: 'sales', label: 'Sales AUD' },
  { key: 'margin', label: 'Margin AUD' },
  { key: 'marginRate', label: '% Margin (adj)', format: 'percent' },
  { key: 'totalMargin', label: 'Total margin AUD' },
  { key: 'totalMarginRate', label: 'Total margin %', format: 'percent' },
  { key: 'packages', label: 'Packages' },
  { key: 'orders', label: 'Orders' },
  // Tableau calls this Room Nights, but it sums nights on every product, including cruises, tours and custom
  // trips, so the label says Nights.
  { key: 'nights', label: 'Nights' },
  { key: 'adr', label: 'ADR (Vendor)' },
  { key: 'leadTime', label: 'Avg lead time' },
  { key: 'los', label: 'Avg LOS', format: 'decimal' },
  { key: 'adults', label: 'No. Adults' },
  { key: 'children', label: 'No. Children' },
  { key: 'infants', label: 'No. Infants' },
];

export function columnsFor(keys: Array<MetricKey>): Array<MetricColumn> {
  return keys.flatMap((key) => {
    const column = metricColumns.find((item) => item.key === key);
    return column ? [column] : [];
  });
}

export function metricText(value: number | null | undefined, format?: MetricColumn['format']): string {
  // A dash for a ratio with no denominator or a value the source does not have.
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '–';
  }
  return new Intl.NumberFormat(
    'en-AU',
    format === 'percent'
      ? { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }
      : { minimumFractionDigits: format === 'decimal' ? 2 : 0, maximumFractionDigits: format === 'decimal' ? 2 : 0 },
  ).format(value);
}
